<template>
  <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="sensorAlertsModal" @keyup.esc="close">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header header">
          <h3 class="modal-title">
            {{ $t('sensorAlertsModal.title') }}
            <span v-if="title" class="modal-subtitle"> — {{ title }}</span>
          </h3>
          <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="!items || items.length === 0" class="small text-muted">
            {{ $t('sensorAlertsModal.empty') }}
          </div>
          <div v-else>
            <div v-for="(item, idx) in items" :key="idx" class="alert-row" :class="rowClass(item)">
              <div class="alert-row-header">
                <span class="severity-badge" :class="severityClass(item.severityCode)">{{ severityLabel(item.severityCode) }}</span>
                <span v-if="item.paused" class="paused-badge">{{ $t('sensorAlertsModal.paused') }}</span>
                <span v-if="item.sensorName" class="context-label">{{ item.sensorName }}</span>
                <span v-if="item.operatorCode != null" class="operator-label">{{ operatorLabel(item.operatorCode) }}</span>
              </div>
              <div class="alert-row-message">
                <strong v-if="item.label">{{ item.label }}</strong>
                <span v-else class="text-muted">{{ $t('sensorAlertsModal.noMessage') }}</span>
              </div>
              <div v-if="hasDetails(item)" class="alert-row-detail small text-muted">
                <span v-if="item.observedValue != null">{{ $t('sensorAlertsModal.observed') }}: <strong>{{ formatValue(item.observedValue) }}</strong></span>
                <span v-if="item.observedValue != null && item.eventValue != null" class="detail-separator">·</span>
                <span v-if="item.eventValue != null">{{ $t('sensorAlertsModal.threshold') }}: {{ formatValue(item.eventValue) }}<span v-if="item.threshold && Number(item.threshold) > 0"> ± {{ formatValue(item.threshold) }}</span></span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">{{ $t('sensorAlertsModal.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'

// Mirrors SensorEventTriggerUtility.AlertEventType server-side.
const OPERATOR_LABELS = {
  0: 'min',
  1: 'max',
  2: 'rising',
  3: 'falling',
  4: 'varying',
  5: 'equals'
}

export default {
  name: 'SensorAlertsModal',
  mixins: [ModalViewMixin],
  props: {
    title: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    modalOpened: { type: String, required: true }
  },
  methods: {
    hasDetails (item) {
      return item.observedValue != null || item.eventValue != null
    },
    severityClass (code) {
      switch (code) {
        case 3: return 'severity-critical'
        case 2: return 'severity-warning'
        case 1: return 'severity-info'
        default: return 'severity-unknown'
      }
    },
    rowClass (item) {
      return item.paused ? 'alert-row-paused' : ''
    },
    severityLabel (code) {
      switch (code) {
        case 3: return this.$t('sensorAlertsModal.severityCritical')
        case 2: return this.$t('sensorAlertsModal.severityWarning')
        case 1: return this.$t('sensorAlertsModal.severityInfo')
        default: return this.$t('sensorAlertsModal.severityUnknown')
      }
    },
    operatorLabel (code) {
      const key = OPERATOR_LABELS[code]
      return key ? this.$t('sensorAlertsModal.operator' + key.charAt(0).toUpperCase() + key.slice(1)) : '?'
    },
    formatValue (value) {
      if (value == null || value === '') return '?'
      const n = Number(value)
      if (Number.isNaN(n)) return value
      return n.toLocaleString(undefined, { maximumFractionDigits: 3 })
    },
    close () {
      this.emitClosed && this.emitClosed()
      Modal.getInstance(document.getElementById('sensorAlertsModal'))?.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-subtitle { font-weight: 400; font-size: 0.9em; opacity: 0.85; }

.alert-row {
  padding: 8px 10px;
  border-left: 4px solid #adb5bd;
  background: #fafafa;
  border-radius: 4px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
}
.alert-row-paused { opacity: 0.7; background: #f1f3f5; }

.alert-row-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; flex-wrap: wrap; }

.severity-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.75em;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.severity-critical { background: #e40000; }
.severity-warning  { background: #ff8000; }
.severity-info     { background: #ffb503; color: #333; }
.severity-unknown  { background: #adb5bd; }

.paused-badge {
  background: #6c757d;
  color: #fff;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
}

.context-label {
  font-size: 0.85em;
  color: #495057;
  font-style: italic;
}

.operator-label {
  margin-left: auto;
  font-size: 0.8em;
  color: #6c757d;
  font-family: 'SF Mono', Menlo, monospace;
}

.alert-row-message { margin-bottom: 4px; }
.alert-row-detail { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.detail-separator { opacity: 0.5; }

// Dark theme: the row backgrounds and secondary text colors are
// tuned for the light card stack; in dark mode the hardcoded light
// greys read as glaring panels and the mid-grey labels go invisible
// against the dark modal body. Severity badges keep their bright
// fills (red / orange / yellow / grey) — they're meaningful in both
// themes and stay legible.
[data-bs-theme="dark"] {
  .alert-row {
    background: #2b2b2b;
    border-left-color: #555;
  }
  .alert-row-paused {
    background: #242424;
  }
  .context-label {
    color: #adb5bd;
  }
  .operator-label {
    color: #6c757d;
  }
}
</style>
